import { useContext, FC } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import styles from './SbSharedRecentlySoldItems.scss';
import { graphql, useFragment } from 'react-relay';
import { SharedItemTile } from '../../shared/SharedItemTile/SharedItemTile';
import { BarHeader } from 'dibs-elements/exports/BarHeader';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy';
import { QuickViewProvider } from '../../global/QuickViewProvider/QuickViewProvider';
import {
    SbSharedThinBrowseItemTrackingProvider,
    SbSharedThinBrowseItemTrackingContext,
} from '../SbSharedThinBrowseItemTrackingProvider/SbSharedThinBrowseItemTrackingProvider';
import { ServerVarsContextConsumer } from '../../global/ServerVarsContext/ServerVarsContext';

import {
    SbSharedRecentlySoldItems_itemSearch$data,
    SbSharedRecentlySoldItems_itemSearch$key,
} from './__generated__/SbSharedRecentlySoldItems_itemSearch.graphql';
import {
    SbSharedRecentlySoldItems_viewer$key,
    SbSharedRecentlySoldItems_viewer$data,
} from './__generated__/SbSharedRecentlySoldItems_viewer.graphql';

type ComponentProps = {
    itemSearch: SbSharedRecentlySoldItems_itemSearch$data;
    isClient: boolean;
    viewer: SbSharedRecentlySoldItems_viewer$data;
};

const SbSharedRecentlySoldItemsComponent: FC<ComponentProps> = ({
    itemSearch,
    isClient,
    viewer,
}) => {
    const { fireThinBrowseItemsImpressionTracking, fireItemClickTracking } = useContext(
        SbSharedThinBrowseItemTrackingContext
    );

    const thinBrowseSoldItems = itemSearch?.soldResults?.edges || [];
    return (
        <ServerVarsContextConsumer>
            {({ isMobile }) => (
                <>
                    <BarHeader
                        title={
                            <FormattedMessage
                                id="sb.SbSharedRecentlySoldItems.title"
                                defaultMessage="Previously Available Items"
                            />
                        }
                    />
                    <QuickViewProvider>
                        {({ quickView }) => {
                            return (
                                <div
                                    className={styles.container}
                                    data-tn="previously-available-items"
                                >
                                    {thinBrowseSoldItems.map((soldItem, index) => {
                                        const { item } = soldItem?.node || {};
                                        if (item) {
                                            return (
                                                <SharedItemTile
                                                    isClient={isClient}
                                                    key={item?.serviceId}
                                                    index={index}
                                                    item={item}
                                                    showSellerName={false}
                                                    viewer={viewer}
                                                    quickView={quickView}
                                                    itemSearch={itemSearch}
                                                    isMobile={isMobile}
                                                    onContentVisible={() =>
                                                        fireThinBrowseItemsImpressionTracking(index)
                                                    }
                                                    onClick={() => fireItemClickTracking(index)}
                                                />
                                            );
                                        } else {
                                            return null;
                                        }
                                    })}
                                </div>
                            );
                        }}
                    </QuickViewProvider>
                </>
            )}
        </ServerVarsContextConsumer>
    );
};

type Props = {
    itemSearch: SbSharedRecentlySoldItems_itemSearch$key;
    isClient: boolean;
    viewer: SbSharedRecentlySoldItems_viewer$key;
};

export const SbSharedRecentlySoldItems: FC<Props> = ({
    itemSearch: itemSearchRef,
    isClient,
    viewer: viewerRef,
}) => {
    const itemSearch = useFragment(
        graphql`
            fragment SbSharedRecentlySoldItems_itemSearch on ItemSearchQueryConnection {
                ...SharedItemTile_itemSearch
                ...SbSharedThinBrowseItemTrackingProvider_itemSearch
                soldResults {
                    edges {
                        node {
                            item {
                                ...SbSharedThinBrowseItemTrackingProvider_items
                                ...SharedItemTile_item
                                    @arguments(
                                        isTrade: $isTrade
                                        userCountryCode: $userCountryCode
                                        priceBookName: $priceBookName
                                        showSeller: $showSeller
                                    )
                                serviceId
                            }
                        }
                    }
                }
            }
        `,
        itemSearchRef
    );
    const viewer = useFragment(
        graphql`
            fragment SbSharedRecentlySoldItems_viewer on Viewer {
                ...SharedItemTile_viewer
                ...SbSharedThinBrowseItemTrackingProvider_viewer
            }
        `,
        viewerRef
    );
    const thinBrowseSoldItems = itemSearch?.soldResults?.edges || [];
    if (thinBrowseSoldItems.length === 0) {
        return null;
    }
    const items = thinBrowseSoldItems?.map(edge => edge?.node?.item).filter(filterFalsy);

    return (
        <SbSharedThinBrowseItemTrackingProvider
            itemSearch={itemSearch}
            viewer={viewer}
            items={items}
            thinBrowseListingType="previously available"
        >
            <SbSharedRecentlySoldItemsComponent
                itemSearch={itemSearch}
                isClient={isClient}
                viewer={viewer}
            />
        </SbSharedThinBrowseItemTrackingProvider>
    );
};
